"use client"

import Link from "next/link"
import { useCallback, useEffect, useState } from "react"
import { ErrorModal } from "./ErrorModal"
import { LoadingBar } from "./LoadingBar"
import { Progress, Stage } from "./Progress"
import { Button } from "@ui/button"
import { FileField } from "../../projects/components/CreateProjectForm/FileField"
import { accepts, usePiiScannerAppV2 } from "../hooks/usePiiScannerAppV2"

interface Props {
    requireTerms?: boolean
}

export function PiiScannerAppV2({ requireTerms }: Props) {
    const { acceptedTerms, acceptTerms, download, error, progress, reset, setFile, stage } = usePiiScannerAppV2({
        requireTerms: requireTerms ?? true,
    })
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false)
    const [acceptTermsBoxChecked, setAcceptTermsBoxChecked] = useState<boolean>(false)

    useEffect(() => {
        if (typeof error === "string") {
            setShowErrorModal(true)
        }
    }, [error])

    const closeErrorModal = useCallback(() => {
        setShowErrorModal(false)
        reset()
    }, [setShowErrorModal, reset])

    const upload = (files: FileList) => {
        if (files) {
            setFile(files[0])
        }
    }

    // If loading, display a skeleton
    if (stage === Stage.Loading) {
        return (
            <div className="flex min-h-80 flex-col space-y-4">
                <div className="flex h-8 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-zinc-800"></div>
                <div className="grow animate-pulse rounded bg-gray-200 dark:bg-zinc-800"></div>
            </div>
        )
    }

    return (
        <>
            {stage === Stage.Waiting &&
                (acceptedTerms === true ? (
                    <form>
                        <FileField label="Data file" upload={upload} accepts={accepts} />
                    </form>
                ) : (
                    <div className="min-h-80">
                        <h2 className="mb-4 text-lg font-semibold">Our terms</h2>
                        <p className="py-2">
                            Before using ResearchWiseAI&apos;s PII Scanner, you must agree to our{" "}
                            <Link
                                className="text-primary-900 dark:text-primary-600 dark:hover:text-primary-500 hover:text-primary-950 hover:underline"
                                target="_blank"
                                href="/policies/terms"
                            >
                                terms & conditions
                            </Link>
                            .
                        </p>
                        <form className="flex flex-wrap">
                            <div className="px-4 py-2">
                                <input
                                    type="checkbox"
                                    name="accept-terms"
                                    id="accept-terms"
                                    className="text-primary-700 focus:ring-primary-700 mr-2 size-4 rounded border-gray-300"
                                    onChange={(e) => {
                                        setAcceptTermsBoxChecked(e.target.checked)
                                    }}
                                />
                                <label htmlFor="accept-terms">
                                    I have understood and accept the{" "}
                                    <Link
                                        className="text-primary-900 dark:text-primary-600 dark:hover:text-primary-500 hover:text-primary-950 hover:underline"
                                        target="_blank"
                                        href="/policies/terms"
                                    >
                                        terms & conditions
                                    </Link>
                                </label>
                            </div>
                            <Button
                                className="mr-0 ml-auto"
                                disabled={!acceptTermsBoxChecked}
                                onClick={() => acceptTerms()}
                                color="primary"
                            >
                                Submit
                            </Button>
                        </form>
                    </div>
                ))}
            {stage !== Stage.Waiting && <Progress stage={stage} />}

            {stage === Stage.Processing && <LoadingBar percentage={progress} />}

            {stage === Stage.Done && (
                <div className="flex justify-center">
                    <div className="mx-auto flex justify-center space-x-5">
                        {/* <pre>{result?.items.map((item) => item.result).join("\n")}</pre> */}
                        {/* Reset button */}
                        <Button className="min-w-48 flex-1" onClick={reset} outline>
                            Reset
                        </Button>
                        {/* Download button */}
                        <Button className="min-w-48 flex-1" onClick={download} color="primary">
                            Download redacted file
                        </Button>
                    </div>
                </div>
            )}
            <ErrorModal open={showErrorModal} close={closeErrorModal}>
                <p className="py-5">{error}</p>
            </ErrorModal>
        </>
    )
}
